// Push Today — drives the daily approve-and-push workflow.
import { ExportCsvButton } from '../../components/ui';
import { EXCLUSION_LABEL } from '../../constants/exclusions.js';
import { downloadCsv } from '../../utils/csv.js';
import { useInfoModal, PageChrome, InfoModal, dqBadgeFor } from './Chrome.jsx';
import { fmtN, fmtH1, SUB_PAGES } from './helpers.js';
import { KpiCard, KpiGrid, SmartText } from './Widgets.jsx';

// Built here: the breakdown is already fully in the dashboard payload.
const CSV_COLUMNS = [
  { key: 'k',     label: 'Reason Code' },
  { key: 'label', label: 'Reason' },
  { key: 'count', label: 'Records' },
];

export function PagePushToday({ stats, nav }) {
  const info = useInfoModal();
  const helpEntry = SUB_PAGES.find((p) => p.id === 'push_today');
  const pq = stats.push_queue || {};
  const failedPush = pq.failed || 0;
  const queuedPush = pq.queued || 0;
  const completedPush = pq.completed || pq.success || 0;

  const breakdown = stats.excluded_breakdown || {};
  // The six inactive_* reasons became mapping GAPS on 2026-06-08 — they show
  // in the gap breakdown now, not here.
  const exclusionRows = [
    'site_excluded',
    'labor_metric_excluded',
    'cost_center_excluded',
    'non_billable_payroll',
    'no_dayforce_context',
    'manually_excluded',
  ].map((k) => ({ k, label: EXCLUSION_LABEL[k] || k, count: breakdown[k] || 0 }))
   .filter((r) => r.count > 0);

  const lines = [];
  if (stats.pending > 0) lines.push(`${fmtN(stats.pending)} records ready for review (${fmtH1(stats.billable_hours)} sync-eligible).`);
  else lines.push('Nothing currently pending. Either everything is approved/synced, or no new data has been ingested.');
  if (stats.queued > 0) lines.push(`${fmtN(stats.queued)} records already approved and queued — sync them from the Records tab.`);
  if (failedPush > 0) lines.push(`${failedPush} NetSuite sync${failedPush === 1 ? '' : 's'} failed — investigate from the Sync Queue.`);

  return (
    <>
      <PageChrome
        title="Sync Today"
        subtitle="Daily workflow: approve pending → sync to NetSuite → confirm success."
        onHome={() => nav.goSubPage('overview')}
        onBack={nav.goBack} canBack={nav.canBack}
        onInfo={info.onOpen}
        dqBadge={dqBadgeFor(stats, () => nav.goSubPage('data_quality'))}
      />

      <KpiGrid>
        <KpiCard
          label="Pending Review" tone="orange"
          value={stats.pending || 0}
          sub={`${fmtH1(stats.billable_hours)} sync-eligible`}
          cta="Review pending" onClick={() => nav.goRecords({ filter: 'pending' })}
        />
        <KpiCard
          label="Approved & Queued" tone="navy"
          value={stats.queued || 0}
          sub={queuedPush > 0 ? `${queuedPush} in NetSuite sync queue` : 'Approved but not yet queued'}
          cta="View queued" onClick={() => nav.goRecords({ filter: 'queued' })}
        />
        <KpiCard
          label="Synced to NetSuite" tone="green"
          value={stats.pushed || 0}
          sub={completedPush > 0 ? `${completedPush} sync queue completed` : 'No sync history yet'}
          cta="View synced" onClick={() => nav.goRecords({ filter: 'pushed' })}
        />
        <KpiCard
          label="Failed Syncs" tone={failedPush > 0 ? 'red' : 'gray'}
          value={failedPush}
          sub={failedPush > 0 ? 'NetSuite rejected — investigate' : 'No failures'}
          cta="Open sync queue" onClick={() => nav.goRecords({ filter: 'push_queue' })}
        />
      </KpiGrid>

      <SmartText lines={lines} />

      {exclusionRows.length > 0 && (
        <div className="db-card">
          <div style={{
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div className="db-card-title">Verify exclusions are intentional</div>
            <ExportCsvButton
              onExport={() => downloadCsv('exclusion-breakdown', exclusionRows, CSV_COLUMNS)}
              title="Download the exclusion breakdown as CSV"
            />
          </div>
          <div className="db-card-sub">
            Records skipped by reference data rules. Periodically confirm the rules are still correct, especially
            after a NetSuite reference change.
          </div>
          <table className="db-table">
            <thead>
              <tr>
                <th>Reason</th>
                <th className="num">Records</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {exclusionRows.map((r) => (
                <tr key={r.k}>
                  <td>{r.label}</td>
                  <td className="num">{fmtN(r.count)}</td>
                  <td className="num">
                    <button type="button" className="btn btn-sm btn-primary"
                      onClick={() => nav.goRecords({ filter: 'excluded_ref_data' })}>
                      View →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <InfoModal open={info.open} onClose={info.onClose} title="About Sync Today" body={helpEntry?.help} />
    </>
  );
}
